import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { PhoneInputWithValidation, PhoneValidationResponse } from '../UI/PhoneInputWithValidation';
import { EmptyCompanionInput } from '../Companions/EmptyCompanionInput';
import { guestAPI, GuestCreateData, CompanionCreateData, CompanionGuestEventCreateData } from '../../services/guestAPI';
import { useAppSelector } from '../../store/hooks';
import { eventAPI } from '../../services/eventAPI';
import { PreviousGuestsModal } from './PreviousGuestsModal';
import { authAPI } from '../../services/api';
import { setUser } from '../../store/slices/authSlice';
import { store } from '../../store/store';
import { hasEventEnded, canEditEvent } from '../../utils/eventUtils';
import { logError } from '../../utils/errorLogger';
import { delay } from '../../utils/delay';
import { useDebounce } from '../../hooks/useDebounce';
import { BillingActionPreview, CostBreakdown } from '../Billing';
import { billingService, PricingConfiguration, HostBalance } from '../../services/billingService';

interface AddGuestFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  eventId: string;
}

// Companion history item type (from backend API)
interface CompanionHistoryItem {
  name: string;
  nickname?: string;
  phone?: string;
  email?: string;
  relationship: 'spouse' | 'partner' | 'child' | 'parent' | 'sibling' | 'friend' | 'colleague' | 'relative' | 'other';
  last_used: string;
  event_count: number;
  events: Array<{
    event_id: string;
    event_title: string;
    event_date: string;
    status: 'accepted' | 'rejected' | 'pending' | 'ignored';
    created_at: string;
    guest_event_id: string;
  }>;
}

export const AddGuestFormEnhanced: React.FC<AddGuestFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  eventId
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const modalRef = useRef<HTMLDivElement>(null);

  // Form data
  const [formData, setFormData] = useState<GuestCreateData>({
    guest_title: '',
    guest_first_name: '',
    guest_last_name: '',
    guest_phone: '',
    guest_email: '',
    companion_limit: undefined,
    allow_companions: false,
    empty_companion_count: 0,
    seat: '',
  });
  
  // Custom title state for "Other" option
  const [customTitle, setCustomTitle] = useState('');

  // Phone validation states
  const [guestPhoneValidation, setGuestPhoneValidation] = useState<PhoneValidationResponse | null>(null);
  const [companionPhoneValidation, setCompanionPhoneValidation] = useState<PhoneValidationResponse | null>(null);
  
  // Companion states
  const [companionHistory, setCompanionHistory] = useState<CompanionHistoryItem[]>([]);
  const [selectedCompanions, setSelectedCompanions] = useState<Set<string>>(new Set());
  const [currentCompanions, setCurrentCompanions] = useState<any[]>([]);
  
  // Companion sub-popup states
  const [showCompanionSubPopup, setShowCompanionSubPopup] = useState(false);
  const [newCompanionData, setNewCompanionData] = useState<CompanionCreateData>({
    name: '',
    phone: '',
    email: '',
    relationship: 'friend',
    notes: ''
  });
  const [companionsToAdd, setCompanionsToAdd] = useState<CompanionCreateData[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Invitation language preference (default English — matches GuestEvent backend default)
  const [inviteInEnglish, setInviteInEnglish] = useState(true);
  
  // Previous guests modal state
  const [showPreviousGuestsModal, setShowPreviousGuestsModal] = useState(false);
  const [addingPreviousGuests, setAddingPreviousGuests] = useState(false);
  
  // Billing state
  const [pricing, setPricing] = useState<PricingConfiguration | null>(null);
  const [balance, setBalance] = useState<HostBalance | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  
  // Phone lookup states
  const [lookupResult, setLookupResult] = useState<{
    found: boolean;
    data?: any;
    message?: string;
  } | null>(null);
  
  // Event ended check and editing permission
  const [eventHasEnded, setEventHasEnded] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [eventHostId, setEventHostId] = useState<string | null>(null);

  // Confirmation modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingPreviousGuestIds, setPendingPreviousGuestIds] = useState<string[]>([]);
  const [pendingCompanionSettings, setPendingCompanionSettings] = useState<Map<string, {
    allow_companions: boolean;
    companion_limit?: number;
    selected_companions: string[];
  }>>(new Map());
  const [isAddingPreviousGuests, setIsAddingPreviousGuests] = useState(false);

  // Fetch companion history and current companions
  const fetchCompanionHistory = useCallback(async (guestId: string) => {
    if (!guestId) return;
    
    try {
      const history = await guestAPI.getCompanionHistory(guestId);
      setCompanionHistory(history.companions || []);
    } catch (err: any) {
      logError('Failed to fetch companion history', err);
      setCompanionHistory([]);
    }
  }, []);

  // Phone lookup function
  const lookupGuestByPhone = useCallback(async (phone: string) => {
    if (!phone || phone.length < 10) return;
    
    try {
      // Pass country code for proper validation - use country from validation if available
      const countryCode = guestPhoneValidation?.country || 'JO';
      const result = await guestAPI.lookupGuestByPhone(eventId, phone, countryCode);
      
      if (result.success && result.data) {
        // Check if the found guest is the event host
        if (eventHostId && result.data.id && String(result.data.id) === eventHostId) {
          setLookupResult({
            found: false,
            message: 'You cannot invite yourself to your own event'
          });
          setError('You cannot invite yourself to your own event');
          return;
        }

        // Check if guest is already invited to THIS event
        if (result.data.already_invited) {
          // Use display_name (nickname) if available, otherwise use first_name + last_name
          const guestDisplayName = result.data.display_name || result.data.nickname || `${result.data.first_name} ${result.data.last_name}`;
          setLookupResult({
            found: false,  // Mark as not usable
            data: result.data,
            message: `${guestDisplayName} is already invited to this event. Please edit the existing invitation instead.`
          });
          setError(`${guestDisplayName} is already invited to this event.`);
          return;
        }

        // Guest found but not invited to this event - OK to proceed
        setLookupResult({
          found: true,
          data: result.data,
          message: 'Existing guest found - information will be auto-filled'
        });

        // Auto-fill form data
        // Use display_name (nickname) if available, otherwise use first_name and last_name
        const displayName = result.data!.display_name || result.data!.nickname;
        let firstName = result.data!.first_name;
        let lastName = result.data!.last_name;
        
        // If we have a display_name (nickname), try to split it intelligently
        if (displayName) {
          // Try to split the display name into first and last name
          // Most names have at least one space, so split on the last space
          const nameParts = displayName.trim().split(/\s+/);
          if (nameParts.length > 1) {
            // Last part is last name, everything else is first name
            lastName = nameParts[nameParts.length - 1];
            firstName = nameParts.slice(0, -1).join(' ');
          } else {
            // Single word - put it all in first name
            firstName = displayName;
            lastName = '';
          }
        }
        
        setFormData(prev => ({
          ...prev,
          guest_first_name: firstName,
          guest_last_name: lastName,
          guest_phone: result.data!.phone,
          guest_email: result.data!.email || '',
        }));
        
        // Fetch companion history for this guest
        if (result.data!.id) {
          fetchCompanionHistory(result.data!.id.toString());
        }
      } else {
        setLookupResult({
          found: false,
          message: result.message || 'No existing guest found with this phone number'
        });
      }
    } catch (err: any) {
      logError('Phone lookup error', err);
      const errorMessage = err.response?.data?.error?.message || 'Failed to lookup guest information';
      setLookupResult({
        found: false,
        message: errorMessage
      });
    }
  }, [eventId, guestPhoneValidation?.country, eventHostId, fetchCompanionHistory]);

  // Handle companion selection from history
  const handleCompanionSelection = (companion: any, isSelected: boolean) => {
    if (eventHasEnded) {
      setError('Cannot select companions for events that have already ended');
      return;
    }
    if (isSelected) {
      // Check if we can add more companions
      if (formData.companion_limit && companionsToAdd.length >= formData.companion_limit) {
        setError(`Cannot add more than ${formData.companion_limit} companions`);
        return;
      }
      
      // Add to selected companions set
      setSelectedCompanions(prev => new Set([...Array.from(prev), companion.name]));
      
      // Add to companions to add list
      const companionToAdd: CompanionCreateData = {
        name: companion.name,
        phone: companion.phone || '',
        email: companion.email || '',
        relationship: companion.relationship,
        notes: ''
      };
      
      setCompanionsToAdd(prev => [...prev, companionToAdd]);
      setError(null); // Clear any previous errors
    } else {
      // Remove from selected companions set
      setSelectedCompanions(prev => {
        const newSet = new Set(prev);
        newSet.delete(companion.name);
        return newSet;
      });
      
      // Remove from companions to add list
      setCompanionsToAdd(prev => prev.filter(c => c.name !== companion.name));
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof GuestCreateData, value: string | number | boolean | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle companion sub-popup
  const handleAddCompanion = () => {
    if (!newCompanionData.name.trim()) {
      setError('Companion name is required');
      return;
    }
    
    // Validate phone number if provided
    // Use normalized phone from validation if available
    let formattedPhone = '';
    if (newCompanionData.phone && newCompanionData.phone.trim()) {
      if (!companionPhoneValidation || !companionPhoneValidation.valid) {
        setError('Please enter a valid phone number for the companion');
        return;
      }
      formattedPhone = companionPhoneValidation.normalized;
    }
    
    setCompanionsToAdd(prev => [...prev, { 
      ...newCompanionData, 
      phone: formattedPhone || newCompanionData.phone 
    }]);
    setNewCompanionData({
      name: '',
      phone: '',
      email: '',
      relationship: 'friend',
      notes: ''
    });
    setCompanionPhoneValidation(null);
    setShowCompanionSubPopup(false);
    setError(null);
  };

  // Fetch event data to check if it has ended and can be edited
  useEffect(() => {
    if (isOpen && eventId) {
      const fetchEvent = async () => {
        try {
          const eventData = await eventAPI.getEvent(eventId);
          setEventHasEnded(hasEventEnded(eventData));
          setCanEdit(canEditEvent(eventData));
          // Store host ID for validation
          if (eventData.host && eventData.host.id) {
            setEventHostId(eventData.host.id);
          }
        } catch (err) {
          logError('Error fetching event data', err);
        }
      };
      fetchEvent();
    }
  }, [isOpen, eventId]);
  
  // Show confirmation modal
  const handleShowConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if event can be edited
    if (!canEdit || eventHasEnded) {
      if (!canEdit) {
        setError('Event editing window has closed or event has ended - cannot add guests');
      } else {
        setError('Cannot add guests to events that have already ended');
      }
      return;
    }
    
    // Validate form (names are optional, but phone or email is required)
    if (!formData.guest_phone && !formData.guest_email) {
      setError('Please provide either a phone number or email address');
      return;
    }
    
    // Fetch billing data BEFORE showing the modal to prevent the "pop" effect
    try {
      setBillingLoading(true);
      const [pricingData, balanceData] = await Promise.all([
        billingService.getCurrentPricing().catch(() => null),
        billingService.getMyBalance().catch(() => null)
      ]);
      setPricing(pricingData);
      setBalance(balanceData);
    } catch (error) {
      logError('Failed to fetch billing data', error);
    } finally {
      setBillingLoading(false);
      // Show confirmation modal only after billing data is loaded
      setShowConfirmModal(true);
    }
  };

  // Form submission after confirmation
  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Get the title value (use custom title if "Other" is selected)
      let titleValue = formData.guest_title;
      if (formData.guest_title === 'Other' && customTitle.trim()) {
        titleValue = customTitle.trim();
      }
      
      // Clean form data - remove empty strings and null values
      const cleanedFormData: any = {
        guest_first_name: formData.guest_first_name?.trim(),
        guest_last_name: formData.guest_last_name?.trim(),
      };
      
      // Add title if provided
      if (titleValue) {
        cleanedFormData.guest_title = titleValue;
      }
      
      // Use normalized phone from validation if available
      if (formData.guest_phone?.trim()) {
        if (guestPhoneValidation && guestPhoneValidation.valid) {
          cleanedFormData.guest_phone = guestPhoneValidation.normalized;
          cleanedFormData.country_code = guestPhoneValidation.country;
        } else {
          setError('Please enter a valid phone number');
          setLoading(false);
          return;
        }
      }
      
      // Only include email if provided
      if (formData.guest_email?.trim()) {
        cleanedFormData.guest_email = formData.guest_email.trim();
      }
      
      // Include companion settings if applicable
      if (formData.allow_companions) {
        cleanedFormData.allow_companions = formData.allow_companions;
        if (formData.companion_limit !== undefined) {
          cleanedFormData.companion_limit = formData.companion_limit;
        }
        // Include empty companion count if set
        if (formData.empty_companion_count && formData.empty_companion_count > 0) {
          cleanedFormData.empty_companion_count = formData.empty_companion_count;
        }
      }
      
      // Add seat if provided
      if (formData.seat?.trim()) {
        cleanedFormData.seat = formData.seat.trim();
      }
      
      // Add invitation language preference
      cleanedFormData.invitation_language = inviteInEnglish ? 'english' : 'arabic';

      // Check if the guest being created is the event host (if guest_id is provided)
      if (cleanedFormData.guest_id && eventHostId && cleanedFormData.guest_id === eventHostId) {
        setError('You cannot invite yourself to your own event');
        setLoading(false);
        return;
      }
      
      const result = await guestAPI.createGuest(eventId, cleanedFormData);
      
      // Check if the created guest is the event host
      if (result && result.guest && result.guest.id && eventHostId && result.guest.id === eventHostId) {
        setError('You cannot invite yourself to your own event');
        setLoading(false);
        return;
      }
      
      if (result && result.id) {
        // Create companions
        const companionErrors: string[] = [];
        let companionsAdded = 0;
        
        for (const companion of companionsToAdd) {
          try {
            // Convert CompanionCreateData to CompanionGuestEventCreateData
            const nameParts = companion.name.trim().split(/\s+/);
            const createData: CompanionGuestEventCreateData = {
              first_name: nameParts[0] || '',
              last_name: nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined,
              phone: companion.phone || undefined,
              email: companion.email || undefined,
              relationship: companion.relationship,
              notes: companion.notes || undefined,
            };
            await guestAPI.companion.createCompanion(eventId, result.id, createData);
            companionsAdded++;
          } catch (companionErr: any) {
            logError('Failed to create companion', companionErr);
            // Extract error message - check multiple possible locations
            let errorMsg = 'Failed to add companion';
            
            // Try to get the meaningful error message
            if (companionErr.response?.data?.error?.message) {
              errorMsg = companionErr.response.data.error.message;
            } else if (companionErr.response?.data?.error?.details) {
              // Check if details is an object with non_field_errors
              const details = companionErr.response.data.error.details;
              if (details.non_field_errors && Array.isArray(details.non_field_errors) && details.non_field_errors.length > 0) {
                errorMsg = details.non_field_errors[0];
              } else if (Array.isArray(details) && details.length > 0) {
                errorMsg = details[0];
              } else if (typeof details === 'string') {
                errorMsg = details;
              } else if (typeof details === 'object') {
                // Try to get first error from any field
                const firstKey = Object.keys(details)[0];
                if (firstKey) {
                  const firstError = details[firstKey];
                  if (Array.isArray(firstError) && firstError.length > 0) {
                    errorMsg = firstError[0];
                  } else if (typeof firstError === 'string') {
                    errorMsg = firstError;
                  }
                }
              }
            } else if (companionErr.response?.data?.message) {
              errorMsg = companionErr.response.data.message;
            } else if (companionErr.message) {
              errorMsg = companionErr.message;
            }
            
            companionErrors.push(`${companion.name || 'Companion'}: ${errorMsg}`);
          }
        }
        
        // Refresh user profile to get updated roles (in case host was added as guest)
        try {
          const profileResponse = await authAPI.getProfile();
          if (profileResponse.data.success && profileResponse.data.data) {
            const updatedUser = {
              ...profileResponse.data.data,
              roles: profileResponse.data.data.roles || [profileResponse.data.data.role],
              active_role: profileResponse.data.data.active_role || profileResponse.data.data.role,
            };
            store.dispatch(setUser(updatedUser));
          }
        } catch (profileErr) {
          logError('Failed to refresh user profile after guest creation', profileErr);
          // Don't fail the guest creation if profile refresh fails
        }
        
        // Show appropriate message based on companion creation results
        if (companionErrors.length > 0) {
          // Some or all companions failed
          if (companionsAdded === 0 && companionsToAdd.length > 0) {
            // All companions failed
            const errorMessage = companionErrors.length === 1 
              ? companionErrors[0]
              : `None of the companions could be added:\n${companionErrors.join('\n')}`;
            setError(errorMessage);
            setLoading(false);
            return; // Don't close the form or show success
          } else {
            // Some companions failed
            const errorMessage = `Guest added successfully, but ${companionErrors.length} companion${companionErrors.length !== 1 ? 's' : ''} could not be added:\n${companionErrors.join('\n')}`;
            setError(errorMessage);
            await delay(2000);
            resetForm();
            onSuccess();
            onClose();
            return;
          }
        }
        
        // All companions added successfully (or no companions to add)
        if (companionsToAdd.length === 0) {
          setSuccess('Guest added successfully!');
        } else {
          setSuccess(`Guest and ${companionsAdded} companion${companionsAdded !== 1 ? 's' : ''} added successfully!`);
        }
        await delay(1500);
        resetForm(); // Clear all fields
        onSuccess();
        onClose();
      } else {
        setError('Failed to add guest');
      }
    } catch (err: any) {
      logError('Guest creation error', err);
        setError(err.response?.data?.error?.message || 'Failed to add guest');
    } finally {
      setLoading(false);
    }
  };

  // Form submission (original - now shows confirmation first)
  const handleSubmit = handleShowConfirmation;

  // Reset form function
  const resetForm = () => {
    setFormData({
      guest_first_name: '',
      guest_last_name: '',
      guest_phone: '',
      guest_email: '',
      companion_limit: undefined,
      allow_companions: false,
      empty_companion_count: 0,
      seat: '',
    });
    setInviteInEnglish(true);
    setLookupResult(null);
    setCompanionHistory([]);
    setCurrentCompanions([]);
    setSelectedCompanions(new Set());
    setCompanionsToAdd([]);
    setError(null);
    setSuccess(null);
  };

  // Reset form
  const handleCancel = () => {
    resetForm();
    onClose();
  };

  // Handle adding previous guests - show confirmation first
  const handlePreviousGuestsSelected = async (
    guestIds: string[], 
    companionSettings?: Map<string, {
      allow_companions: boolean;
      companion_limit?: number;
      selected_companions: string[];
    }>
  ) => {
    if (guestIds.length === 0) return;
    
    // Check if any selected guest is the event host
    if (eventHostId && guestIds.includes(eventHostId)) {
      setError('You cannot invite yourself to your own event');
      setShowPreviousGuestsModal(false);
      return;
    }
    
    // Store the selected guest IDs and companion settings
    setPendingPreviousGuestIds(guestIds);
    setPendingCompanionSettings(companionSettings || new Map());
    setShowPreviousGuestsModal(false);
    setIsAddingPreviousGuests(true);
    setError(null);
    
    // Fetch billing data BEFORE showing the modal to prevent the "pop" effect
    try {
      setBillingLoading(true);
      const [pricingData, balanceData] = await Promise.all([
        billingService.getCurrentPricing().catch(() => null),
        billingService.getMyBalance().catch(() => null)
      ]);
      setPricing(pricingData);
      setBalance(balanceData);
    } catch (error) {
      logError('Failed to fetch billing data', error);
    } finally {
      setBillingLoading(false);
      // Show confirmation modal only after billing data is loaded
      setShowConfirmModal(true);
    }
  };

  // Actually add previous guests after confirmation
  const handleConfirmPreviousGuests = async () => {
    setShowConfirmModal(false);
    setAddingPreviousGuests(true);
    setError(null);
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const guestId of pendingPreviousGuestIds) {
        // Double-check: prevent host from inviting themselves
        if (eventHostId && guestId === eventHostId) {
          failCount++;
          continue;
        }
        
        try {
          // Get companion settings for this guest
          const settings = pendingCompanionSettings.get(guestId);
          
          // Create guest event using the guest_id with companion settings
          const guestData: any = {
            guest_id: guestId,
          };
          
          // Add companion settings if provided
          if (settings) {
            // Explicitly set allow_companions (true or false)
            guestData.allow_companions = settings.allow_companions || false;
            if (settings.allow_companions && settings.companion_limit !== undefined) {
              guestData.companion_limit = settings.companion_limit;
            }
          } else {
            // Explicitly set to false if no settings provided
            guestData.allow_companions = false;
          }
          
          const result = await guestAPI.createGuest(eventId, guestData);
          
          // Import selected companions if allow_companions is true and companions are selected
          if (settings?.allow_companions && settings?.selected_companions && settings.selected_companions.length > 0 && result?.id) {
            try {
              // Fetch companion history for this guest
              const companionHistory = await guestAPI.getCompanionHistory(guestId);
              
              if (companionHistory.companions && companionHistory.companions.length > 0) {
                // Valid relationship values
                const validRelationships = ['spouse', 'partner', 'child', 'parent', 'sibling', 'friend', 'colleague', 'relative', 'other'] as const;
                type ValidRelationship = typeof validRelationships[number];
                
                // Create a map of companion names to their data for quick lookup
                const companionMap = new Map<string, typeof companionHistory.companions[0]>();
                companionHistory.companions.forEach(comp => {
                  companionMap.set(comp.name, comp);
                });
                
                // Only import selected companions
                for (const companionName of settings.selected_companions) {
                  const companion = companionMap.get(companionName);
                  if (!companion) {
                    // Companion not found in history, skipping
                    continue;
                  }
                  
                  try {
                    // Validate and cast relationship to ensure it's a valid type
                    const relationship: ValidRelationship = validRelationships.includes(companion.relationship as ValidRelationship)
                      ? (companion.relationship as ValidRelationship)
                      : 'other';
                    
                    // Convert to CompanionGuestEventCreateData
                    const nameParts = companion.name.trim().split(/\s+/);
                    const createData: CompanionGuestEventCreateData = {
                      first_name: nameParts[0] || '',
                      last_name: nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined,
                      phone: companion.phone || undefined,
                      email: companion.email || undefined,
                      relationship: relationship,
                      notes: '',
                    };
                    await guestAPI.companion.createCompanion(eventId, result.id, createData);
                  } catch (companionErr: any) {
                    logError(`Failed to import companion ${companion.name}`, companionErr);
                    // Continue with other companions even if one fails
                  }
                }
              }
            } catch (historyErr: any) {
              logError(`Failed to fetch/import companion history for guest ${guestId}`, historyErr);
              // Don't fail the guest creation if companion import fails
            }
          }
          
          successCount++;
        } catch (err: any) {
          logError(`Failed to add guest ${guestId}`, err);
          // Check if error is about host self-invitation
          const errorMessage = err.response?.data?.error?.message || err.message || '';
          if (errorMessage.includes('host') && errorMessage.includes('own event')) {
            setError('You cannot invite yourself to your own event');
          }
          failCount++;
        }
      }
      
      if (successCount > 0) {
        setSuccess(
          failCount > 0 
            ? `Added ${successCount} guests successfully. ${failCount} failed.` 
            : `Added ${successCount} guest(s) successfully!`
        );
        await delay(1500);
        setPendingPreviousGuestIds([]);
        setPendingCompanionSettings(new Map());
        setIsAddingPreviousGuests(false);
        onSuccess();
        onClose();
      } else {
        setError('Failed to add guests. Please try again.');
        setIsAddingPreviousGuests(false);
      }
    } catch (err: any) {
      logError('Error adding previous guests', err);
      setError(err.response?.data?.error?.message || 'Failed to add guests');
      setIsAddingPreviousGuests(false);
    } finally {
      setAddingPreviousGuests(false);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, eventId]);

  // Debounced phone lookup for guest phone
  const debouncedPhone = useDebounce(formData.guest_phone, 500);
  
  useEffect(() => {
    if (debouncedPhone && debouncedPhone.length >= 10) {
      lookupGuestByPhone(debouncedPhone);
    } else {
      setLookupResult(null);
      setCompanionHistory([]);
      setCurrentCompanions([]);
    }
  }, [debouncedPhone, lookupGuestByPhone]);

  // Reset billing data when modal closes
  useEffect(() => {
    if (!showConfirmModal) {
      // Reset billing data when modal closes to ensure fresh data on next open
      setPricing(null);
      setBalance(null);
    }
  }, [showConfirmModal]);

  // Calculate costs for billing preview
  const calculateCosts = useCallback((): CostBreakdown[] => {
    if (!pricing) return [];

    const costs: CostBreakdown[] = [];
    
    if (isAddingPreviousGuests) {
      // For previous guests: calculate based on selected guests and their companions
      const guestCount = pendingPreviousGuestIds.length;
      let totalCompanions = 0;
      
      pendingCompanionSettings.forEach((settings) => {
        if (settings.allow_companions && settings.selected_companions) {
          totalCompanions += settings.selected_companions.length;
        }
      });

      if (guestCount > 0) {
        costs.push({
          label: `Guest Base Fee (${guestCount} guest${guestCount !== 1 ? 's' : ''})`,
          amount: guestCount * parseFloat(pricing.guest_base_fee),
          quantity: guestCount,
          unitPrice: parseFloat(pricing.guest_base_fee)
        });
      }

      if (totalCompanions > 0) {
        costs.push({
          label: `Companion Fee (${totalCompanions} companion${totalCompanions !== 1 ? 's' : ''})`,
          amount: totalCompanions * parseFloat(pricing.companion_fee),
          quantity: totalCompanions,
          unitPrice: parseFloat(pricing.companion_fee)
        });
      }
    } else {
      // For single guest addition
      costs.push({
        label: 'Guest Base Fee',
        amount: parseFloat(pricing.guest_base_fee),
        quantity: 1,
        unitPrice: parseFloat(pricing.guest_base_fee)
      });

      // Add detailed companions
      if (companionsToAdd.length > 0) {
        costs.push({
          label: `Companion Fee (${companionsToAdd.length} companion${companionsToAdd.length !== 1 ? 's' : ''})`,
          amount: companionsToAdd.length * parseFloat(pricing.companion_fee),
          quantity: companionsToAdd.length,
          unitPrice: parseFloat(pricing.companion_fee)
        });
      }

      // Add empty companions
      const emptyCompanionCount = formData.empty_companion_count || 0;
      if (emptyCompanionCount > 0) {
        costs.push({
          label: `Empty Companion Fee (${emptyCompanionCount} empty companion${emptyCompanionCount !== 1 ? 's' : ''})`,
          amount: emptyCompanionCount * parseFloat(pricing.companion_fee),
          quantity: emptyCompanionCount,
          unitPrice: parseFloat(pricing.companion_fee)
        });
      }
    }

    return costs;
  }, [pricing, isAddingPreviousGuests, pendingPreviousGuestIds, pendingCompanionSettings, companionsToAdd, formData.empty_companion_count]);


  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        ref={modalRef}
        className={`rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto shadow-2xl ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}
        style={{
          position: 'relative',
          transform: 'translateY(0)',
          margin: 'auto'
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-xl font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Add New Guest
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark' 
                ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {(!canEdit || eventHasEnded) && (
          <div className={`p-4 mb-4 rounded-lg border ${
            theme === 'dark' ? 'bg-yellow-900/20 border-yellow-700/50' : 'bg-yellow-50 border-yellow-200'
          }`}>
            <p className={`text-sm font-medium ${
              theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'
            }`}>
              ⚠️ {!canEdit 
                ? 'Event editing window has closed or event has ended. You cannot add guests at this time.'
                : 'This event has already ended. You cannot add guests to ended events.'}
            </p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Select from Previous Guests Button */}
          <div className="mb-4">
            <Button
              type="button"
              onClick={() => setShowPreviousGuestsModal(true)}
              variant="outline"
              className="w-full"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              Select from Previous Guests
            </Button>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
            <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>OR</span>
            <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
          </div>

          {/* Phone Number */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Phone Number
            </label>
            <PhoneInputWithValidation
              value={formData.guest_phone || ''}
              onChange={(phoneValue, countryCodeValue) => {
                handleInputChange('guest_phone', phoneValue);
              }}
              onValidationChange={setGuestPhoneValidation}
              showValidationResult={false}
              placeholder="Enter phone number"
              dropdownZIndex={10000}
            />
            
            {/* Phone lookup result */}
            {lookupResult && (
              <div className={`mt-2 p-2 rounded text-sm ${
                lookupResult.found 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
              }`}>
                {lookupResult.message}
              </div>
            )}
          </div>

          {/* Custom Title Input (shown when "Other" is selected) */}
          {formData.guest_title === 'Other' && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Custom Title *
              </label>
              <Input
                type="text"
                value={customTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomTitle(e.target.value)}
                placeholder="Enter custom title"
                maxLength={50}
                required
                variant="mirsal"
              />
            </div>
          )}

          {/* Title - First Name - Last Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Title
              </label>
              <select
                value={formData.guest_title}
                onChange={(e) => {
                  handleInputChange('guest_title', e.target.value);
                  if (e.target.value !== 'Other') {
                    setCustomTitle('');
                  }
                }}
                className={`w-full rounded-md shadow-sm focus:ring-2 focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm transition-all duration-200 px-2 py-2 border ${
                  theme === 'dark' 
                    ? 'border-gray-600 bg-gray-800 text-white' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
              >
                <option value="">Select</option>
                <option value="Dr">Dr</option>
                <option value="Mr">Mr</option>
                <option value="Ms">Ms</option>
                <option value="Mrs">Mrs</option>
                <option value="Prof">Prof</option>
                <option value="Eng">Eng</option>
                <option value="Rev">Rev</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                First Name
              </label>
              <Input
                type="text"
                value={formData.guest_first_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  handleInputChange('guest_first_name', e.target.value)
                }
                placeholder="Enter first name (optional)"
                variant="mirsal"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Last Name
              </label>
              <Input
                type="text"
                value={formData.guest_last_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  handleInputChange('guest_last_name', e.target.value)
                }
                placeholder="Enter last name (optional)"
                variant="mirsal"
              />
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Email Address
            </label>
            <Input
              type="email"
              value={formData.guest_email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                handleInputChange('guest_email', e.target.value)
              }
              placeholder="guest@example.com"
              variant="mirsal"
            />
            <p className={`text-xs mt-1 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              * Either phone number or email is required
            </p>
          </div>

          {/* Companion Settings */}
          <div className="border-t pt-4">
            <h3 className={`text-sm font-medium mb-3 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Companion Settings
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allow_companions"
                  checked={formData.allow_companions}
                  onChange={(e) => handleInputChange('allow_companions', e.target.checked)}
                  className={`mr-3 rounded ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-mirsal-green focus:ring-mirsal-green' 
                      : 'bg-white border-gray-300 text-mirsal-green focus:ring-mirsal-green'
                  }`}
                />
                <label htmlFor="allow_companions" className={`text-sm ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Allow Companions
                </label>
              </div>

              {formData.allow_companions && (
                <>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Companion Limit
                    </label>
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      value={formData.companion_limit || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        handleInputChange('companion_limit', e.target.value ? parseInt(e.target.value) : undefined)
                      }
                      placeholder="Enter companion limit (0-10)"
                      variant="mirsal"
                    />
                    <p className={`text-xs mt-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Maximum number of companions this guest can bring
                    </p>
                  </div>

                  {/* Empty Companions Input */}
                  <div className="mt-4">
                    <EmptyCompanionInput
                      value={formData.empty_companion_count || 0}
                      onChange={(count) => handleInputChange('empty_companion_count', count)}
                      companionLimit={formData.companion_limit}
                      detailedCompanionCount={companionsToAdd.length}
                      disabled={!formData.allow_companions || loading}
                      label="Add empty companions (count only, no details required)"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Companion Information - Enhanced Design */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Companion Information
              </h3>
              {formData.allow_companions && formData.companion_limit && formData.companion_limit > 0 && (
                <div className={`text-xs px-2 py-1 rounded ${
                  theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                }`}>
                  {currentCompanions.length + companionsToAdd.length} / {formData.companion_limit} companions
                </div>
              )}
            </div>

            {/* When companions are enabled, show existing companions and add button */}
            {formData.allow_companions && (
              <div className="space-y-4">
                {/* Existing Companions */}
                {(currentCompanions.length > 0 || companionHistory.length > 0) && (
                  <div className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <h4 className={`text-sm font-medium mb-3 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Existing Companions
                    </h4>
                    
                    {/* Current Companions */}
                    {currentCompanions.length > 0 && (
                      <div className="mb-3">
                        <div className={`text-xs font-medium mb-2 ${
                          theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`}>
                          Current Event ({currentCompanions.length})
                        </div>
                        <div className="space-y-2">
                          {currentCompanions.map((companion, index) => (
                            <div key={index} className={`flex items-center justify-between p-2 border rounded ${
                              theme === 'dark' ? 'bg-blue-900 border-blue-600' : 'bg-blue-50 border-blue-200'
                            }`}>
                              <div className="flex-1">
                                <div className={`text-sm font-medium ${
                                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {companion.name}
                                </div>
                                <div className={`text-xs ${
                                  theme === 'dark' ? 'text-blue-400' : 'text-blue-500'
                                }`}>
                                  {companion.relationship}
                                  {companion.phone && ` • ${companion.phone}`}
                                  {companion.email && ` • ${companion.email}`}
                                </div>
                              </div>
                              <div className={`ml-2 px-2 py-1 text-xs rounded ${
                                theme === 'dark' ? 'bg-blue-600 text-blue-100' : 'bg-blue-200 text-blue-800'
                              }`}>
                                Already Added
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Previous Companions */}
                    {companionHistory.length > 0 && (
                      <div>
                        <div className={`text-xs font-medium mb-2 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Previous Events ({companionHistory.length})
                        </div>
                        <div className="space-y-2">
                          {companionHistory.map((companion, index) => (
                            <div key={index} className={`flex items-center justify-between p-2 border rounded ${
                              theme === 'dark' ? 'bg-gray-700 border-gray-500' : 'bg-white border-gray-300'
                            }`}>
                              <div className="flex-1">
                                <div className={`text-sm font-medium ${
                                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {companion.nickname || companion.name}
                                  {companion.nickname && companion.name !== companion.nickname && (
                                    <span className={`text-xs ml-1 ${
                                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                    }`}>
                                      ({companion.name})
                                    </span>
                                  )}
                                </div>
                                <div className={`text-xs ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  {companion.relationship} • Used {companion.event_count} time{companion.event_count === 1 ? '' : 's'}
                                  {companion.phone && ` • ${companion.phone}`}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleCompanionSelection(companion, !selectedCompanions.has(companion.name))}
                                disabled={eventHasEnded || !canEdit}
                                className={`ml-2 px-3 py-1 text-xs rounded ${
                                  eventHasEnded
                                    ? 'opacity-50 cursor-not-allowed bg-gray-400 text-gray-200'
                                    : selectedCompanions.has(companion.name)
                                      ? 'bg-mirsal-green text-white'
                                      : theme === 'dark'
                                        ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                title={eventHasEnded ? "Cannot select companions for events that have already ended" : ""}
                              >
                                {selectedCompanions.has(companion.name) ? 'Selected' : 'Select'}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                  </div>
                )}

                {/* Add Another Button */}
                {formData.companion_limit && formData.companion_limit > 0 && companionsToAdd.length < formData.companion_limit && (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => setShowCompanionSubPopup(true)}
                      className={`px-6 py-3 text-sm font-medium rounded-lg transition-colors ${
                        theme === 'dark'
                          ? 'bg-mirsal-green text-white hover:bg-green-600'
                          : 'bg-mirsal-green text-white hover:bg-green-600'
                      }`}
                    >
                      + Add Another Companion
                    </button>
                  </div>
                )}

                {/* Companions to Add List */}
                {formData.companion_limit && formData.companion_limit > 0 && companionsToAdd.length > 0 && (
                  <div className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-green-900 border-green-600' : 'bg-green-50 border-green-200'
                  }`}>
                    <h4 className={`text-sm font-medium mb-3 ${
                      theme === 'dark' ? 'text-green-300' : 'text-green-700'
                    }`}>
                      Companions to Add ({companionsToAdd.length})
                    </h4>
                    <div className="space-y-2">
                      {companionsToAdd.map((companion, index) => (
                        <div key={index} className={`flex items-center justify-between p-2 border rounded ${
                          theme === 'dark' ? 'bg-green-800 border-green-500' : 'bg-white border-green-300'
                        }`}>
                          <div className="flex-1">
                            <div className={`text-sm font-medium ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                              {companion.name}
                            </div>
                            <div className={`text-xs ${
                              theme === 'dark' ? 'text-green-400' : 'text-green-500'
                            }`}>
                              {companion.relationship}
                              {companion.phone && ` • ${companion.phone}`}
                              {companion.email && ` • ${companion.email}`}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setCompanionsToAdd(prev => prev.filter((_, i) => i !== index));
                            }}
                            className={`ml-2 px-2 py-1 text-xs rounded ${
                              theme === 'dark' ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-200 text-red-800 hover:bg-red-300'
                            }`}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Message when companions enabled but no limit set */}
            {formData.allow_companions && (!formData.companion_limit || formData.companion_limit === 0) && (
              <div className={`p-4 rounded-lg border ${
                theme === 'dark' ? 'bg-yellow-900 border-yellow-600' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="text-sm text-center">
                  <span className={`${
                    theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                  }`}>
                    Set a companion limit above to add new companions
                  </span>
                </div>
              </div>
            )}

            {/* When companions are disabled, show existing companions or message */}
            {!formData.allow_companions && (
              <div className="space-y-4">
                {/* Show existing companions even when disabled */}
                {(currentCompanions.length > 0 || companionHistory.length > 0) ? (
                  <div className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <h4 className={`text-sm font-medium mb-3 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Existing Companions
                      <span className={`text-xs font-normal ml-2 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        (Enable companions above to manage)
                      </span>
                    </h4>
                    
                    {/* Current Companions */}
                    {currentCompanions.length > 0 && (
                      <div className="mb-3">
                        <div className={`text-xs font-medium mb-2 ${
                          theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`}>
                          Current Event ({currentCompanions.length})
                        </div>
                        <div className="space-y-2">
                          {currentCompanions.map((companion, index) => (
                            <div key={index} className={`flex items-center justify-between p-2 border rounded ${
                              theme === 'dark' ? 'bg-blue-900 border-blue-600' : 'bg-blue-50 border-blue-200'
                            }`}>
                              <div className="flex-1">
                                <div className={`text-sm font-medium ${
                                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {companion.name}
                                </div>
                                <div className={`text-xs ${
                                  theme === 'dark' ? 'text-blue-400' : 'text-blue-500'
                                }`}>
                                  {companion.relationship}
                                  {companion.phone && ` • ${companion.phone}`}
                                  {companion.email && ` • ${companion.email}`}
                                </div>
                              </div>
                              <div className={`ml-2 px-2 py-1 text-xs rounded ${
                                theme === 'dark' ? 'bg-blue-600 text-blue-100' : 'bg-blue-200 text-blue-800'
                              }`}>
                                Already Added
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Previous Companions */}
                    {companionHistory.length > 0 && (
                      <div>
                        <div className={`text-xs font-medium mb-2 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Previous Events ({companionHistory.length})
                        </div>
                        <div className="space-y-2">
                          {companionHistory.map((companion, index) => (
                            <div key={index} className={`flex items-center justify-between p-2 border rounded ${
                              theme === 'dark' ? 'bg-gray-700 border-gray-500' : 'bg-white border-gray-300'
                            }`}>
                              <div className="flex-1">
                                <div className={`text-sm font-medium ${
                                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {companion.name}
                                </div>
                                <div className={`text-xs ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  {companion.relationship} • Used {companion.event_count} time{companion.event_count === 1 ? '' : 's'}
                                  {companion.phone && ` • ${companion.phone}`}
                                </div>
                              </div>
                              <div className={`ml-2 px-2 py-1 text-xs rounded ${
                                theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                              }`}>
                                View Only
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="text-sm text-center">
                      <span className={`${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Enable companions above to manage companion information
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Seat Assignment */}
          <div className="border-t pt-4">
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Seat Number
            </label>
            <Input
              type="text"
              value={formData.seat || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                handleInputChange('seat', e.target.value)
              }
              placeholder="e.g., A12, Table 5, VIP-1"
              variant="mirsal"
            />
            <p className={`text-xs mt-1 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Optional: Assign a seat number or identifier for this guest
            </p>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {success && (
            <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {/* Invitation Language Preference */}
          <div className="flex items-center space-x-2 pt-2">
            <input
              type="checkbox"
              id="invite-in-english-enhanced"
              checked={inviteInEnglish}
              onChange={(e) => setInviteInEnglish(e.target.checked)}
              className={`w-4 h-4 rounded border-gray-300 ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 text-mirsal-green focus:ring-mirsal-green' 
                  : 'text-mirsal-green focus:ring-mirsal-green'
              }`}
            />
            <label 
              htmlFor="invite-in-english-enhanced"
              className={`text-sm font-medium cursor-pointer ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Invite in English
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || eventHasEnded || !canEdit || (!formData.guest_phone && !formData.guest_email)}
              title={!canEdit || eventHasEnded ? "Cannot add guests - event editing is not allowed" : ""}
            >
              {loading ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999]">
          <div className={`rounded-lg p-6 w-full max-w-md mx-4 shadow-2xl ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Confirm Guest Addition
              </h3>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  if (isAddingPreviousGuests) {
                    setIsAddingPreviousGuests(false);
                    setPendingPreviousGuestIds([]);
                    setPendingCompanionSettings(new Map());
                  }
                }}
                className={`p-1 rounded transition-colors ${
                  theme === 'dark' 
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Billing Cost Preview - Show this first and prominently */}
              {!billingLoading && pricing ? (
                <BillingActionPreview
                  costs={calculateCosts()}
                  showBalance={true}
                  showBalanceAfter={true}
                  currentBalance={balance}
                  pricing={pricing}
                  showWarning={true}
                  compact={false}
                  title="Cost Preview"
                  infoText="Fees are charged immediately when guests are added."
                />
              ) : (
                <div className={`p-4 rounded-lg border ${
                  theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Loading cost information...
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowConfirmModal(false);
                  if (isAddingPreviousGuests) {
                    setIsAddingPreviousGuests(false);
                    setPendingPreviousGuestIds([]);
                    setPendingCompanionSettings(new Map());
                  }
                }}
                disabled={loading || addingPreviousGuests}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={isAddingPreviousGuests ? handleConfirmPreviousGuests : handleConfirmSubmit}
                disabled={loading || addingPreviousGuests}
              >
                {loading || addingPreviousGuests 
                  ? 'Adding...' 
                  : isAddingPreviousGuests 
                    ? `Confirm & Add ${pendingPreviousGuestIds.length} Guest${pendingPreviousGuestIds.length !== 1 ? 's' : ''}`
                    : 'Confirm & Add Guest'
                }
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Companion Sub-Popup */}
      {showCompanionSubPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <div className={`rounded-lg p-6 w-full max-w-md mx-4 shadow-2xl ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Add Companion
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Name *
                </label>
                <Input
                  type="text"
                  value={newCompanionData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setNewCompanionData(prev => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Enter companion name"
                  variant="mirsal"
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Relationship *
                </label>
                <select
                  value={newCompanionData.relationship}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                    setNewCompanionData(prev => ({ ...prev, relationship: e.target.value as any }))
                  }
                  className={`w-full px-3 py-2 border rounded-md ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="spouse">Spouse</option>
                  <option value="partner">Partner</option>
                  <option value="sibling">Sibling</option>
                  <option value="parent">Parent</option>
                  <option value="child">Child</option>
                  <option value="friend">Friend</option>
                  <option value="colleague">Colleague</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Phone Number
                </label>
                <PhoneInputWithValidation
                  value={newCompanionData.phone || ''}
                  onChange={(phoneValue, countryCodeValue) => {
                    setNewCompanionData(prev => ({ ...prev, phone: phoneValue }));
                  }}
                  onValidationChange={setCompanionPhoneValidation}
                  showValidationResult={false}
                  placeholder="Enter phone number (optional)"
                  dropdownZIndex={10000}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Email Address
                </label>
                <Input
                  type="email"
                  value={newCompanionData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setNewCompanionData(prev => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="Enter email address (optional)"
                  variant="mirsal"
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Notes
                </label>
                <textarea
                  value={newCompanionData.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                    setNewCompanionData(prev => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Additional notes (optional)"
                  rows={2}
                  className={`w-full px-3 py-2 border rounded-md ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowCompanionSubPopup(false);
                  setNewCompanionData({
                    name: '',
                    phone: '',
                    email: '',
                    relationship: 'friend',
                    notes: ''
                  });
                  setCompanionPhoneValidation(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleAddCompanion}
                disabled={!newCompanionData.name.trim()}
              >
                Add Companion
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {/* Previous Guests Modal */}
      <PreviousGuestsModal
        isOpen={showPreviousGuestsModal}
        onClose={() => setShowPreviousGuestsModal(false)}
        eventId={eventId}
        onGuestsSelected={handlePreviousGuestsSelected}
      />
      
      {/* Loading indicator for adding previous guests */}
      {addingPreviousGuests && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10002]">
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6`}>
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                Adding guests...
              </span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
